import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UniThemeProvider } from '@planne-software/uni/themes';
import {
  SeatMap as PlanneSeatMap,
  SeatMapOverview as PlanneSeatMapOverview,
  I18nProvider,
  SelectedTariff as SeatMapSelectedTariff,
  SeatAllocation,
  SeatMapSkeleton,
  SeatMapEventType,
  AllocationChangePayload,
  SeatChangePayload,
  AreaChangePayload,
  AllocationStatus,
} from '@planne-software/seat-map';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR as localeText } from '@mui/x-date-pickers/locales';
import { ptBR } from 'date-fns/locale';
import { SeatMapSlotResource, Scheduling, ScheduleTime } from '@src/core/buyerApiClient';
import { useAppSelector, useAppDispatch, useWidgetParam } from '../../core/hooks';
import {
  currentProductAdderStatus,
  isCurrentStepValid,
  goToNextStep,
  loadSeatMap,
  previousProductAddStep,
  productAdderStepState,
  productSelector,
  schedulingSeatMapLoadingSelector,
  schedulingSeatMapSelector,
  SelectedResource,
  tariffsState,
} from '@src/stores/product';
import { serializePricingTariffSelections, tariffsRequireQuantityPricing, SelectedTariff } from '@src/domains/tariffs';
import useTranslate from '../../i18n/useTranslate';
import { track } from '@src/services/analytics';
import { getAllocationsExtrasCents, transformSeatMapForUI } from '@src/common/seatMaps';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';

interface PlaneSeatmapRenderProps {
  scheduling: Scheduling;
  timeMinutes: ScheduleTime | null;
  onSelectionChange: (resources: SelectedResource[], isReady: boolean, exceedingValueCents: number) => void;
  selectedTariffs: SelectedTariff[];
}

const PlaneSeatmapRender = (props: Readonly<PlaneSeatmapRenderProps>) => {
  const { scheduling, timeMinutes, onSelectionChange, selectedTariffs } = props;

  const { locale, i18n } = useTranslate();
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { listOfTariffsPerType } = useAppSelector(tariffsState(widgetId));
  const anySelected = listOfTariffsPerType.some((t) => t.selectedQuantity > 0);
  const gated = listOfTariffsPerType.some((t) => t.schedulings.some((s) => tariffsRequireQuantityPricing(s)));
  const selections = gated && anySelected ? serializePricingTariffSelections(listOfTariffsPerType) : '';

  const seatMap = useAppSelector(schedulingSeatMapSelector(scheduling.id, timeMinutes?.minutes, selections));
  const loadingSeatMap = useAppSelector(
    schedulingSeatMapLoadingSelector(scheduling.id, timeMinutes?.minutes, selections),
  );
  const handleGoBack = () => dispatch(previousProductAddStep(widgetId));

  const [allocationFromMap, setAllocationFromMap] = useState<SeatAllocation[]>([]);
  const [allocationStatus, setAllocationStatus] = useState<AllocationStatus>(AllocationStatus.INCOMPLETE);

  const transformedSeatMap = useMemo(() => (seatMap ? transformSeatMapForUI(seatMap) : undefined), [seatMap]);

  const status = useAppSelector(currentProductAdderStatus(widgetId));
  const cartAddCtaText = useWidgetParam('cart-add-cta-text');
  const stepIsValid = useAppSelector(isCurrentStepValid(widgetId));
  const { isAtLast } = useAppSelector(productAdderStepState(widgetId));
  const productId = useSelectedProductId();
  const product = useAppSelector(productSelector(productId))!;

  const handleGoForwards = () => {
    if (!isAtLast) {
      track('productAdderNextStep', {
        productId: productId,
        productName: product.name,
      });
    }

    dispatch(goToNextStep(widgetId));
  };

  const primaryButtonText = useMemo(() => {
    if (isAtLast) {
      return cartAddCtaText ? cartAddCtaText : i18n('adder_purchaseButton');
    }

    return i18n('adder_goForwardsFooter');
  }, [cartAddCtaText, i18n, isAtLast]);

  const seatMapSelectedTariffs = useMemo(
    () => selectedTariffs.map(({ tariff, quantity }) => ({ tariff, quantity })) as unknown as SeatMapSelectedTariff[],
    [selectedTariffs],
  );

  // Use allocation status from the library instead of calculating manually
  const isSelectionComplete = useMemo(() => allocationStatus === AllocationStatus.VALID, [allocationStatus]);

  const disableButton = useMemo(
    () => !stepIsValid || status === 'adding_to_cart' || !isSelectionComplete,
    [stepIsValid, status, isSelectionComplete],
  );

  const totalExtrasCents = useMemo(() => getAllocationsExtrasCents(allocationFromMap), [allocationFromMap]);

  const handleSeatMapEvent = useCallback(
    (eventType: SeatMapEventType, payload: AllocationChangePayload | SeatChangePayload | AreaChangePayload) => {
      if (eventType === SeatMapEventType.ALLOCATION_CHANGE) {
        const allocationPayload = payload as AllocationChangePayload;
        setAllocationFromMap(allocationPayload.seatAllocations);
        setAllocationStatus(allocationPayload.status);
      }
    },
    [],
  );

  // Memoize resources to send to parent
  const selectedResourcesForParent = useMemo(
    () =>
      allocationFromMap.map(({ seat: { resource }, tariffs }) => ({
        resourceSlot: resource as unknown as SeatMapSlotResource,
        tariffs: tariffs.map((tariff: { id: string }) => ({
          tariffId: tariff.id,
          quantity: 1,
        })),
      })),
    [allocationFromMap],
  );

  // Track if component has initialized to prevent calling onSelectionChange on mount
  const hasNotifiedInitialState = useRef(false);

  useEffect(() => {
    // Skip initial call since useLayoutEffect handles mount
    if (!hasNotifiedInitialState.current) {
      hasNotifiedInitialState.current = true;
      return;
    }

    onSelectionChange(selectedResourcesForParent, isSelectionComplete, totalExtrasCents);
  }, [isSelectionComplete, totalExtrasCents, selectedResourcesForParent]);

  useEffect(() => {
    dispatch(loadSeatMap(scheduling.id, timeMinutes?.minutes, selections));
  }, [dispatch, scheduling.id, timeMinutes?.minutes, selections]);

  // Reset selection on mount only
  useLayoutEffect(() => {
    onSelectionChange([], false, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingSeatMap || (!transformSeatMapForUI && seatMap)) {
    return <SeatMapSkeleton showActions />;
  }

  if (!transformedSeatMap || !seatMap) {
    return null;
  }

  return (
    <UniThemeProvider
      adapterLocale={ptBR}
      dateAdapter={AdapterDateFns}
      localeText={localeText.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <I18nProvider defaultLanguage={locale}>
        <PlanneSeatMap
          showActions
          loading={loadingSeatMap}
          seatMapOverview={{
            name: 'Seat Map',
            id: transformedSeatMap.id,
            areas: transformedSeatMap.areas as unknown as PlanneSeatMapOverview['areas'],
            categories: seatMap.categories as unknown as PlanneSeatMapOverview['categories'],
          }}
          onEvent={handleSeatMapEvent}
          selectedTariffs={seatMapSelectedTariffs}
          actions={{
            primaryButton: {
              disabled: disableButton,
              onClick: handleGoForwards,
              children: isAtLast ? primaryButtonText : i18n('adder_goForwardsFooter'),
            },
            secondaryButton: {
              onClick: handleGoBack,
            },
          }}
        />
      </I18nProvider>
    </UniThemeProvider>
  );
};

export default PlaneSeatmapRender;
